// Library Methods
import { useMemo } from "react";

// Components
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import { SxProps } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Styles
import {
  CustomIconButtonConfirm,
  CustomIconButtonDark,
  CustomIconButtonError,
  CustomIconButtonGray,
  CustomIconButtonLight,
} from "src/styles/mui/muiStylesHelper";
import { UI_SIZES } from "src/constants/uiTokens";

type CustomIconButtonType = {
  isDisabled?: boolean;
  isLoading?: boolean;
  tooltipTitle?: string;
  variant?: "light" | "gray" | "dark" | "error" | "confirm";
  icon: JSX.Element;
  customSx?: SxProps;
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

const CustomIconButton = ({
  isDisabled = false,
  isLoading = false,
  tooltipTitle = "",
  variant = "dark",
  customSx = {},
  icon,
  onClick,
}: CustomIconButtonType) => {
  const theme = useTheme();

  const iconButtonVariant = useMemo(() => {
    switch (variant) {
      case "light":
        return CustomIconButtonLight;
      case "gray":
        return CustomIconButtonGray;
      case "error":
        return CustomIconButtonError;
      case "confirm":
        return CustomIconButtonConfirm;
      default:
        return CustomIconButtonDark;
    }
  }, [variant]);

  if (isLoading) {
    return (
      <Skeleton
        variant="rounded"
        height={UI_SIZES.iconButtonSize}
        width={UI_SIZES.iconButtonSize}
        sx={{
          borderRadius: "4px",
          minHeight: `${UI_SIZES.iconButtonSize}px`,
          minWidth: `${UI_SIZES.iconButtonSize}px`,
        }}
      />
    );
  }

  // Plain object spreading only merges at the top level, so a customSx
  // with its own "&:hover"/"&:disabled" would otherwise replace the
  // variant's entirely instead of adding to it (e.g. losing its disabled
  // backgroundColor to just gain a border). Merging those two keys
  // explicitly lets a caller layer extra disabled/hover styles on top.
  const variantSx = iconButtonVariant(theme) as Record<string, unknown>;
  const extraSx = (customSx ?? {}) as Record<string, unknown>;

  return (
    <Tooltip title={tooltipTitle}>
      <span>
        <IconButton
          disabled={isDisabled}
          data-testid={tooltipTitle}
          aria-label={tooltipTitle}
          onClick={onClick}
          sx={{
            ...variantSx,
            ...extraSx,
            "&:hover": {
              ...(variantSx["&:hover"] as object),
              ...(extraSx["&:hover"] as object),
            },
            "&:disabled": {
              ...(variantSx["&:disabled"] as object),
              ...(extraSx["&:disabled"] as object),
            },
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default CustomIconButton;
